import { useMemo } from 'react';
import type { OrderMessage, OrderTrack, TrackStage } from '@capsico/contracts';
import type { Translate } from '../i18n';
import { clockOfDay } from '../utils/format';
import { etaDisplay, isMoving, RIDER_VISIBLE_STAGES } from './liveOrderMerge';
import { LOCAL_PREFIX } from './useOrderChat';
import type { ChatBubble, StepView } from '../components/Tracking';

/** Stages that still happen before a rider is on the way (the "finding a rider" window). */
const BEFORE_RIDER: readonly TrackStage[] = ['placed', 'accepted', 'preparing', 'ready'];

/**
 * Turns an OrderTrack into exactly what the tracking screen prints (titles, ETA strings,
 * stepper rows, rider captions, which cards show). Keeps every decision out of the components.
 */
export function useTrackView(track: OrderTrack | null, now: number, t: Translate) {
  return useMemo(() => {
    if (!track) return null;
    const moving = isMoving(track);
    const riderName = track.rider?.name ?? null;
    const restaurant = track.restaurant.name;

    const subtitle = (() => {
      switch (track.stage) {
        case 'placed':
          return t('track.sub.placed', { name: restaurant });
        case 'accepted':
          return t('track.sub.accepted', { name: restaurant });
        case 'preparing':
          return t('track.sub.preparing', { name: restaurant });
        case 'ready':
          return track.rider ? t('track.sub.ready') : t('track.sub.findingRider');
        case 'rider_assigned':
          return t('track.sub.rider_assigned', { rider: riderName ?? t('track.pinRider') });
        case 'picked_up':
          return t('track.sub.picked_up', { rider: riderName ?? t('track.pinRider') });
        case 'nearby':
          return t('track.sub.nearby', { rider: riderName ?? t('track.pinRider') });
        case 'delivered':
          return t('track.sub.delivered');
        case 'cancelled':
          return null;
      }
    })();

    const eta = etaDisplay(track, now);
    const etaValue =
      eta.kind === 'range'
        ? t('track.etaRange', { from: eta.from, to: eta.to })
        : eta.kind === 'countdown'
          ? t('track.etaMinutes', { minutes: eta.minutes })
          : eta.kind === 'late'
            ? t('track.late')
            : null;

    const currentIndex = track.steps.findIndex((step) => step.stage === track.stage);
    const steps: StepView[] = track.steps.map((step, index) => ({
      key: step.stage,
      label: t(`track.step.${step.stage}`),
      time: step.at ? clockOfDay(step.at) : null,
      done: step.done,
      current: moving && index === currentIndex,
      negative: step.stage === 'cancelled' && step.done,
    }));

    const riderVisible = RIDER_VISIBLE_STAGES.includes(track.stage);
    const point = (lat: number | null, lng: number | null) =>
      lat !== null && lng !== null ? { lat, lng } : null;
    const restaurantPoint = point(track.restaurant.lat, track.restaurant.lng);
    const dropPoint = point(track.drop.lat, track.drop.lng);
    const riderPoint =
      riderVisible && track.riderLocation
        ? { lat: track.riderLocation.lat, lng: track.riderLocation.lng }
        : null;

    const rider = track.rider;
    const vehicleText = rider
      ? [
          rider.vehicleClass ? t(`track.vehicle.${rider.vehicleClass}`) : null,
          rider.vehicleTail ? `•••${rider.vehicleTail}` : null,
        ]
          .filter(Boolean)
          .join(' · ') || null
      : null;

    return {
      moving,
      title: t(`track.stage.${track.stage}`),
      subtitle,
      etaCaption: eta.kind === 'late' ? null : t('track.etaArriving'),
      etaValue,
      late: eta.kind === 'late',
      steps,
      showMap: moving && (restaurantPoint !== null || dropPoint !== null),
      map: { restaurant: restaurantPoint, drop: dropPoint, rider: riderPoint },
      findingRider: !rider && BEFORE_RIDER.includes(track.stage),
      showRider: moving && rider !== null,
      rider: rider
        ? {
            name: rider.name,
            photoUrl: rider.photoUrl,
            ratingText: rider.rating !== null ? rider.rating.toFixed(1) : null,
            vehicleText,
          }
        : null,
      showCode: moving && track.deliveryCodeRequired && !track.leaveAtDoor,
      showLeaveAtDoor: moving && track.leaveAtDoor,
      showProof: track.stage === 'delivered' && track.leaveAtDoor && !!track.proofPhotoUrl,
      proofPhotoUrl: track.proofPhotoUrl,
      lateApology: track.lateApology !== null,
      cancelled: track.stage === 'cancelled',
      chatOpen: track.chatOpen,
    };
  }, [track, now, t]);
}

/** Chat messages → bubbles (time "7:42 PM"; optimistic ones marked pending). */
export function chatBubbles(messages: OrderMessage[]): ChatBubble[] {
  return messages.map((message) => ({
    id: message.id,
    body: message.body,
    time: clockOfDay(message.at),
    mine: message.mine,
    pending: message.id.startsWith(LOCAL_PREFIX),
  }));
}
